import { createSlice, type PayloadAction, nanoid } from '@reduxjs/toolkit';
import type { ClientState } from '@/features/app/client/clientSlice';

export type PhotoDraft = {
  id: string;
  base64: string;
  mime: string;
  note?: string;
};

export type DeviceDraft = {
  tempId: string;
  // Campos de orden (alineado a API futura)
  imei_serie: string;
  diagnostic?: string;
  reported_failure?: string;
  // order_status_id: number;
  has_lock: boolean;
  lock_type: number; // 0=none, 1=password, 2=pattern (temporal, cat desde API)
  lock_info?: string; // password o patrón serializado
  warranty_id: number; // cat desde API (temporal mapping)
  repair_cost: number;
  total_cost: number;
  paid_amount: number;
  payment_method_id?: number | null;
  // Flag derivado del nombre del método (true si es "Efectivo")
  payment_method_is_cash?: boolean | null;
  equipment_type_id: number; // cat desde API (temporal mapping)
  equipment_model_id: number;
  equipment_brand_id: number;
  assigned_to_user_id: number | null;

  // UI extra
  photos: PhotoDraft[];

  // Persistencia de creacion para evitar duplicados
  service_order_id?: number | null;
  service_order_code?: string | null;
  signature_done?: boolean;
  photos_done?: boolean;
  payment_done?: boolean;
};

type Step = 1 | 2 | 3 | 4; // cliente, equipo, fotos, presupuesto

export type ReceptionState = {
  idempotencyKey: string;
  step: Step;
  clientDraft: ClientState | null; // guardamos el draft del cliente
  currentDevice: string | null; // tempId del dispositivo en edición
  devices: DeviceDraft[]; // dispositivos (incluye el actual si existe)
  createdClientId?: number | null; // id del cliente creado
  lastIntakeId?: number | null; // id del intake registrado cuando aplica
  lastIntakeCode?: string | null; // código del intake
  lastTicketCache?: { key: string; pdf_base64: string } | null; // cache del último PDF
};

const emptyDevice = (): DeviceDraft => ({
  tempId: nanoid(),
  imei_serie: '',
  diagnostic: '',
  reported_failure: '',
  // order_status_id: 1,
  has_lock: false,
  lock_type: 0,
  lock_info: '',
  warranty_id: 0,
  repair_cost: 0,
  total_cost: 0,
  paid_amount: 0,
  payment_method_id: null,
  payment_method_is_cash: null,
  equipment_type_id: 0,
  equipment_model_id: 0,
  equipment_brand_id: 0,
  assigned_to_user_id: null,
  photos: [],
  service_order_id: null,
  service_order_code: null,
  signature_done: false,
  photos_done: false,
  payment_done: false,
});

const initialState: ReceptionState = {
  idempotencyKey:
    typeof crypto !== 'undefined' && 'randomUUID' in crypto
      ? crypto.randomUUID()
      : `key_${nanoid()}`,
  step: 1,
  clientDraft: null,
  currentDevice: null,
  devices: [],
  createdClientId: null,
  lastIntakeId: null,
  lastIntakeCode: null,
  lastTicketCache: null,
};

const receptionSlice = createSlice({
  name: 'reception',
  initialState,
  reducers: {
    // Paso 1: cliente
    setClientSnapshot(state, action: PayloadAction<ClientState>) {
      state.clientDraft = action.payload;
      state.step = 2;
    },

    // Establece el id del cliente creado para evitar duplicacion
    setCreatedClientId(state, action: PayloadAction<number | null | undefined>) {
      state.createdClientId = action.payload ?? null;
    },

    // Paso 2: datos del equipo (parcial)
    upsertCurrentDevice(state, action: PayloadAction<Partial<DeviceDraft>>) {
      // Asegura currentDevice en devices
      let idx = state.currentDevice
        ? state.devices.findIndex((d) => d.tempId === state.currentDevice)
        : -1;
      if (idx === -1) {
        const d = emptyDevice();
        state.devices.push(d);
        state.currentDevice = d.tempId;
        idx = state.devices.length - 1;
      }
      // Merge parcial sobre el dispositivo actual
      state.devices[idx] = { ...state.devices[idx], ...action.payload };
      state.step = 3; // siguiente: fotos
    },

    // Paso 3→4: fotos se aplican hasta presupuesto
    setPhotosToCurrent(state, action: PayloadAction<PhotoDraft[]>) {
      if (!state.currentDevice) {
        const d = emptyDevice();
        state.devices.push(d);
        state.currentDevice = d.tempId;
      }
      const idx = state.devices.findIndex((d) => d.tempId === state.currentDevice);
      if (idx !== -1) state.devices[idx].photos = action.payload;
    },

    // Paso 4: presupuesto
    setBudgetToCurrent(
      state,
      action: PayloadAction<{
        total_cost: number;
        paid_amount: number;
        repair_cost: number;
        payment_method_id?: number | null;
        payment_method_is_cash?: boolean | null;
      }>,
    ) {
      if (!state.currentDevice) {
        const d = emptyDevice();
        state.devices.push(d);
        state.currentDevice = d.tempId;
      }
      const idx = state.devices.findIndex((d) => d.tempId === state.currentDevice);
      if (idx !== -1) {
        state.devices[idx].total_cost = action.payload.total_cost;
        state.devices[idx].paid_amount = action.payload.paid_amount;
        state.devices[idx].repair_cost = action.payload.repair_cost;
        state.devices[idx].payment_method_id = action.payload.payment_method_id ?? null;
        state.devices[idx].payment_method_is_cash =
          typeof action.payload.payment_method_is_cash === 'boolean'
            ? action.payload.payment_method_is_cash
            : null;
      }
      state.step = 4;
    },

    // Cerrar el equipo actual y agregar a la lista
    finalizeCurrentDevice(state) {
      if (!state.currentDevice) return;
      // El dispositivo ya está en devices; solo liberamos el current
      state.currentDevice = null;
      state.step = 2; // si agregas otro, vuelves a equipo
    },

    // Terminar sin agregar otro equipo (pero metiendo el actual si falta)
    finalizeAll(state) {
      // Nada que empujar: el actual ya vive en devices
      state.currentDevice = null;
    },

    // Establece como actual el último dispositivo agregado (para ediciones)
    setCurrentToLastDevice(state) {
      if (state.devices.length > 0) {
        const last = state.devices[state.devices.length - 1];
        state.currentDevice = last.tempId;
      }
    },

    goToStep(state, action: PayloadAction<Step>) {
      state.step = action.payload;
    },

    // Reset explícito al finalizar
    resetReception(state) {
      const newKey =
        typeof crypto !== 'undefined' && 'randomUUID' in crypto
          ? crypto.randomUUID()
          : `key_${nanoid()}`;
      state.idempotencyKey = newKey;
      state.step = 1;
      state.clientDraft = null;
      state.currentDevice = null;
      state.devices = [];
      state.createdClientId = null;
      state.lastIntakeId = null;
      state.lastIntakeCode = null;
      state.lastTicketCache = null;
    },

    // Setear id de orden para un dispositivo por indice o tempId
    setDeviceOrderId(
      state,
      action: PayloadAction<{
        index?: number;
        tempId?: string;
        orderId: number;
        orderCode?: string | null;
      }>,
    ) {
      const { index, tempId, orderId, orderCode } = action.payload;
      let idx =
        typeof index === 'number' ? index : state.devices.findIndex((d) => d.tempId === tempId);
      if (idx >= 0 && idx < state.devices.length) {
        state.devices[idx].service_order_id = orderId;
        if (typeof orderCode !== 'undefined') {
          state.devices[idx].service_order_code = orderCode ?? null;
        }
      }
    },

    markSignatureDoneByOrderId(state, action: PayloadAction<number>) {
      const oid = action.payload;
      const d = state.devices.find((x) => x.service_order_id === oid);
      if (d) d.signature_done = true;
    },
    markPhotosDoneByOrderId(state, action: PayloadAction<number>) {
      const oid = action.payload;
      const d = state.devices.find((x) => x.service_order_id === oid);
      if (d) d.photos_done = true;
    },
    markPaymentDoneByOrderId(state, action: PayloadAction<number>) {
      const oid = action.payload;
      const d = state.devices.find((x) => x.service_order_id === oid);
      if (d) d.payment_done = true;
    },

    // Guardar datos del último intake
    setLastIntake(state, action: PayloadAction<{ id: number | null; code?: string | null }>) {
      state.lastIntakeId = action.payload.id ?? null;
      state.lastIntakeCode = action.payload.code ?? null;
    },

    // Guardar/limpiar el PDF del último ticket generado
    setLastPrintedTicket(state, action: PayloadAction<{ key: string; pdf_base64: string }>) {
      state.lastTicketCache = { key: action.payload.key, pdf_base64: action.payload.pdf_base64 };
    },
    clearLastPrintedTicket(state) {
      state.lastTicketCache = null;
    },
  },
});

export const {
  setClientSnapshot,
  upsertCurrentDevice,
  setPhotosToCurrent,
  setBudgetToCurrent,
  finalizeCurrentDevice,
  finalizeAll,
  goToStep,
  resetReception,
  setCreatedClientId,
  setDeviceOrderId,
  markSignatureDoneByOrderId,
  markPhotosDoneByOrderId,
  markPaymentDoneByOrderId,
  setCurrentToLastDevice,
  setLastIntake,
  setLastPrintedTicket,
  clearLastPrintedTicket,
} = receptionSlice.actions;

export default receptionSlice.reducer;
